import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Spinner } from "@aigntx/ui";
import { endpoints } from "../api";
import type { TenantRecord } from "../types";

export function RecordsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TenantRecord[] | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    endpoints
      .listRecords()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  async function remove(id: string) {
    await endpoints.deleteRecord(id);
    setRows((rs) => (rs ? rs.filter((r) => r.id !== id) : rs));
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!rows)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  return (
    <div className="page">
      <header className="page__head between">
        <div>
          <h1 className="page__title">{t("records.title")}</h1>
          <p className="page__sub">{t("records.subtitle")}</p>
        </div>
        <Button onClick={() => setOpen(true)}>{t("records.new")}</Button>
      </header>

      {rows.length === 0 ? (
        <Card>
          <p className="muted">{t("records.empty")}</p>
        </Card>
      ) : (
        <div className="rec-grid">
          {rows.map((r) => (
            <Card key={r.id} className="rec">
              <div className="rec__head">
                <Badge tone="accent">{r.kind}</Badge>
                <button className="rec__del" onClick={() => void remove(r.id)} aria-label="Delete">
                  ✕
                </button>
              </div>
              <div className="rec__title">{r.title}</div>
              {r.body && <div className="rec__body">{r.body}</div>}
              <div className="rec__meta mono">
                {r.createdBy} · {new Date(r.createdAt).toLocaleDateString()}
              </div>
            </Card>
          ))}
        </div>
      )}

      {open && (
        <RecordModal
          onClose={() => setOpen(false)}
          onSaved={(rec) => {
            setOpen(false);
            setRows((rs) => (rs ? [rec, ...rs] : [rec]));
          }}
        />
      )}
    </div>
  );
}

function RecordModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (r: TenantRecord) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("lead");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onSaved(await endpoints.createRecord({ title, kind, body }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("records.new")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.create")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <Field label={t("records.recTitle")}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label={t("records.kind")}>
        <Input value={kind} onChange={(e) => setKind(e.target.value)} />
      </Field>
      <Field label={t("records.body")}>
        <Input value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
    </Modal>
  );
}
